import { useGoogleLogin } from '@react-oauth/google';
import { Eye, EyeOff } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import CheckBox from 'src/components/CheckBox';

import GoogleLogo from '../../assets/img/google_logo.png';
import MicrosoftLogo from '../../assets/img/microsoft-icon.svg';
import ButtonCmp from '../../components/ButtonCmp';
import ErrorMsg from '../../components/errorMsg';
import InputText from '../../components/InputText/InputText';
import { ROUTES } from '../../constants/routes';
import { setIsAuthenticated } from '../../redux/auth.slice';
import { type AppDispatch } from '../../redux/store';
import { microsoftLogin, login } from '../../services/AuthService';
import WalToast from '../../utils/WalToast';
import './login.scss';
import AuthWrapper from '../AppLayout/AuthWrapper';

import CustomerSelection from './CustomerSelection';
import VerifyOtp from './VerifyOtp';

const initValidForm = {
    email: true,
    isEmailValid: true,
    password: true,
    emailNotExists: true,
    inCorrectPassword: true,
};

const Login = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();

    const [validForm, setValidForm] = useState(initValidForm);
    const [isLoading, setIsLoading] = useState(false);
    const initFormData: any = {
        email: '',
        password: '',
        rememberMe: false,
    };
    const navigate = useNavigate();
    const location = useLocation();

    const [formData, setFormData] = useState<any>(initFormData);
    const [errorMsg, setErrorMsg] = useState<any>('');
    const [resError, setResError] = useState<any>(null);
    const [showVerification, setShowVerification] = useState(false);

    const [showPassword, setShowPassword] = useState(false);
    // WAL internal-user flow: MicrosoftCallback redirects here with state.showCustomerSelection
    // after the backend responds requiresCustomerSelection (selection token already stored).
    const [showCustomerSelection, setShowCustomerSelection] = useState(false);

    const [isUserExists, setIsUserExists] = useState<any>(false);
    const [userName, setUserName] = useState<any>('');

    // Function to get time-based greeting
    const getTimeBasedGreeting = () => {
        const currentHour = new Date().getHours();

        if (currentHour >= 5 && currentHour < 12) {
            return t('Auth.Good_Morning');
        } else if (currentHour >= 12 && currentHour < 17) {
            return t('Auth.Good_Afternoon');
        } else {
            return t('Auth.Good_Evening');
        }
    };

    useEffect(() => {
        if (window.performance.navigation.type === 1) {
            navigate(location.pathname, { replace: true });
        }

        if (location.state?.error) {
            WalToast.error(location.state.error);
            navigate(location.pathname, { replace: true });
        }

        if (location.state?.showCustomerSelection) {
            setShowCustomerSelection(true);
            // Consume the history state so a refresh lands back on the normal login form
            navigate(location.pathname, { replace: true });
        }
    }, []);

    const handleInputChange = (event: any) => {
        if (location.state?.message) {
            navigate(location.pathname, { replace: true });
        }

        if (event.target.name === 'email') {
            setValidForm({
                ...validForm,
                email: true,
                isEmailValid: true,
                emailNotExists: true,
            });
            setFormData({ ...formData, [event.target.name]: event.target.value });
        } else if (event.target.name === 'password') {
            setValidForm({
                ...validForm,
                password: true,
                inCorrectPassword: true,
                emailNotExists: true,
            });
            setFormData({ ...formData, [event.target.name]: event.target.value });
        } else if (event.target.name === 'rememberMe') {
            setFormData({ ...formData, rememberMe: event.target.checked });
        }

        setErrorMsg('');
        setResError(null);
    };

    const valid = () => {
        let isValid = true;
        const validFormData = {
            email: true,
            isEmailValid: true,
            password: true,
        };
        setErrorMsg(null);
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

        if (formData.email === '' || formData.email === null) {
            validFormData.email = false;
            isValid = false;
        }

        if (formData.email && !emailRegex.test(formData.email)) {
            validFormData.isEmailValid = false;
            isValid = false;
        }

        const pwdValid = formData?.password?.trim() === '' ? false : true;

        if (!pwdValid && isUserExists) {
            validFormData.password = false;
            isValid = false;
        }
        setValidForm((prevValidForm) => {
            const updatedState = { ...prevValidForm, ...validFormData };

            return updatedState;
        });

        return isValid;
    };

    const handleSubmit = (event: any) => {
        setErrorMsg('');

        event.preventDefault();

        if (!valid()) {
            return true;
        }
        setIsLoading(true);

        const payload: any = {
            email: formData?.email,
        };

        if (isUserExists) {
            payload.password = formData?.password;
            payload.rememberMe = formData?.rememberMe;
        }

        login(payload)
            .then((response) => {
                setIsLoading(false);

                if (response?.token) {
                    dispatch(setIsAuthenticated(true) as any);

                    if (!response?.data?.isSetBasicDetail && response?.data?.isMainUser) {
                        navigate(ROUTES.SETUP_ACCOUNT);
                    } else {
                        navigate(ROUTES.HOME);
                    }

                    return true;
                }

                if (response?.isUserActive) {
                    setValidForm({ ...validForm, emailNotExists: true });
                    setIsUserExists(true);
                    setUserName(response?.userName);
                } else {
                    setShowVerification(true);
                }
            })
            .catch((error): any => {
                setIsLoading(false);

                setResError(error?.response?.data);
                setErrorMsg(error?.response?.data?.message || t('Auth.Error.Login_Failed'));
            });
    };

    const handleGoogleLogin = useGoogleLogin({
        flow: 'auth-code',
        ux_mode: 'redirect',
        redirect_uri: `${window.WEB_APP_URL}/callback`,
    });

    const microsoftAuthHandler = () => {
        microsoftLogin()
            .then((response: any) => {
                if (response?.data?.url) {
                    window.location.href = response?.data?.url;
                } else {
                    navigate(ROUTES.LOGIN, {
                        state: {
                            message: t('Auth.Error.Microsoft_Login_Failed'),
                        },
                    });
                }
            })
            .catch((error: any): any => {
                navigate(ROUTES.LOGIN, {
                    state: {
                        message: error?.response?.data?.message || t('Auth.Error.Microsoft_Auth_Error'),
                    },
                });
            });
    };

    return (
        <>
            {showCustomerSelection ? (
                <CustomerSelection
                    onBack={() => {
                        setShowCustomerSelection(false);
                        localStorage.removeItem('token');
                    }}
                />
            ) : !showVerification ? (
                <AuthWrapper
                    authHeading={
                        isUserExists ? (
                            <div className="flex flex-wrap">
                                <span>{getTimeBasedGreeting()}</span>
                                <span className="text-grayLight600 pl-1.5">{userName}</span>
                            </div>
                        ) : (
                            t('Auth.Get_Started')
                        )
                    }
                    authSubTitle={isUserExists ? t('Auth.Welcome_Credentials') : t('Auth.Welcome_Details')}
                >
                    <>
                        <form onSubmit={handleSubmit} className="flex flex-col gap-y-5">
                            <div>
                                <InputText
                                    id="email"
                                    inputName="email"
                                    size="md"
                                    label={!isUserExists ? '' : t('Auth.Email')}
                                    labelClassName={!isUserExists ? '' : 'block mb-1.5'}
                                    className={`max-sm:!py-[7px] max-sm:font-normal ${resError && resError?.type === 'email' ? ' !border-[#FDA29B] ' : ' !border-[#D0D5DD] '}`}
                                    placeholder={t('Auth.Enter_Email')}
                                    value={formData.email}
                                    onChangeFunc={handleInputChange}
                                    autoFocus={true}
                                    autoComplete="email"
                                />
                                {(!isUserExists || (resError && resError?.type === 'email')) && <ErrorMsg errorText={errorMsg} />}
                            </div>

                            {isUserExists && (
                                <div>
                                    <InputText
                                        label={t('Auth.Password')}
                                        id="password"
                                        labelClassName="block mb-1.5"
                                        inputType={showPassword ? 'text' : 'password'}
                                        inputName="password"
                                        className={`pr-[36px] ${
                                            !validForm.password || !validForm?.inCorrectPassword || (resError && resError?.type === 'password') ? ' !border-[#FDA29B] ' : ' !border-[#D0D5DD] '
                                        }`}
                                        size="md"
                                        placeholder={t('Auth.Enter_Password')}
                                        value={formData.password}
                                        onChangeFunc={handleInputChange}
                                        maxLength={50}
                                        icon={
                                            <div
                                                className="absolute top-1/2 -translate-y-1/2 right-3 cursor-pointer peer-placeholder-shown:pointer-events-none peer-placeholder-shown:text-gray400"
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                {showPassword ? <Eye className="w-4 h-4 text-gray400 object-contain" /> : <EyeOff className="w-4 h-4 text-gray400 object-contain" />}
                                            </div>
                                        }
                                    />
                                    {resError && resError?.type === 'password' && <ErrorMsg errorText={errorMsg} />}
                                    {isUserExists && (
                                        <div className="flex justify-between items-center mt-2">
                                            <CheckBox
                                                id={`rememberMe`}
                                                labelHtmlFor={`rememberMe`}
                                                onChangeFunc={handleInputChange}
                                                name="rememberMe"
                                                value={formData.rememberMe}
                                                label="Remember me"
                                                labelClassName="!text-grayLight600 !font-normal !text-[12px] leading-[18px]"
                                                checked={formData.rememberMe}
                                                parentClassName="items-center"
                                            />
                                            <Link className="text-primary700 text-[12px] leading-[18px] font-medium cursor-pointer" to={ROUTES.FORGOT_PASSWORD} state={{ email: formData.email }}>
                                                {t('Auth.Forgot_Password?')}
                                            </Link>
                                        </div>
                                    )}
                                </div>
                            )}

                            <ButtonCmp
                                type="submit"
                                size="lg"
                                disabled={isLoading || !formData.email || !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email) || (isUserExists && !formData.password)}
                                className="btn_primary_black w-full max-sm:!py-[7px]"
                                loading={isLoading}
                            >
                                {t('Auth.Continue')}
                            </ButtonCmp>
                        </form>

                        <div className="flex items-center mt-4">
                            <div className="h-[1px] flex-1 bg-[#F2F4FC]"></div>
                            <p className="text-[14px] leading-[20px] mx-2 max-sm:mx-1 text-textSecondary font-medium uppercase">{t('Auth.Or')}</p>
                            <div className="h-[1px] flex-1 bg-[#F2F4FC]"></div>
                        </div>
                        <ButtonCmp
                            type="button"
                            className="mt-4 !gap-2 btn_secondary_black max-sm:!h-8 max-sm:!py-0 max-sm:!px-4 max-sm:!gap-3 max-sm:rounded-lg"
                            size="md"
                            onClick={() => handleGoogleLogin()}
                        >
                            <img src={GoogleLogo} className="h-[18px] max-sm:h-5 max-sm:w-5 object-contain" alt="logo" />
                            {t('Auth.Sign_in_with_Google')}
                        </ButtonCmp>
                        <ButtonCmp
                            type="button"
                            className="mt-4 max-sm:mt-2.5 mb-4 !gap-2 btn_secondary_black max-sm:!h-8 max-sm:!py-0 max-sm:!px-4 max-sm:!gap-3 max-sm:rounded-lg"
                            size="md"
                            onClick={() => microsoftAuthHandler()}
                        >
                            <img src={MicrosoftLogo} className="h-[18px] max-sm:h-5 max-sm:w-5 object-contain" alt="logo" />
                            {t('Auth.Sign_in_with_Microsoft')}
                        </ButtonCmp>

                        {location.state?.message && <ErrorMsg errorText={location.state.message} />}
                    </>
                </AuthWrapper>
            ) : (
                <VerifyOtp email={formData?.email} login={true} setShowVerification={setShowVerification} />
            )}
        </>
    );
};

export default Login;
